// The P3 cell order (APP_VISIBILITY §3e, cell-runs): a draw unit's triangles
// ordered by the 128 m WORLD cell of their centroid, in Morton order, each
// cell an element range of the one index (or of the non-indexed soup) with
// the AABB of what its triangles can draw; viscull.js draws the visible runs.
// Pure (no three): the ground worker (gmworker.js), groundtree.js,
// buildings.js, forestvol.js and scripts/check-vis.mjs share it.
import { decD } from '../../groundm/stitch.js';

export const CELL_M = 128;    // a cell run's cell
export const MAX_RUNS = 4;    // draws of one unit at most (the smallest gaps merge)
const BLOCK_BITS = 6;         // a Morton prefix: an aligned 8 × 8 block of cells (1024 m)

// Morton key of the world cell (i, j) (10 bits each, offset 512: an aligned
// 2^b × 2^b block of cells is one contiguous key range)
const spread = (v) => {
  v &= 0x3ff;
  v = (v | (v << 8)) & 0x00ff00ff; v = (v | (v << 4)) & 0x0f0f0f0f;
  v = (v | (v << 2)) & 0x33333333; v = (v | (v << 1)) & 0x55555555;
  return v;
};
export const cellKey = (i, j) => spread(i + 512) | (spread(j + 512) << 1);

// a triangle list's 128 m cell order: `pos` world x / y / z per vertex
// (Float32), `idx` the index (null: a non-indexed soup, triangle t = vertices
// 3t … 3t + 2), `dy` per vertex: the vertex draws anywhere in [y, y + dy]
// (the ground's geomorph; null: 0), `padY` added to every box's top (a VS
// lift), `pad` around every box (float rounding). The cell of a triangle is
// its centroid's; a cell's box is the AABB of every vertex of its triangles
// (so a triangle crossing a cell line is inside its cell's box); `cell` the
// cell side (m, default CELL_M; the far tree's coarse nodes: groundCells). → null for a
// single cell, else { order: Uint32Array (new triangle → old), cells: [{ key,
// start, count (elements: 3 per triangle), lo, hi }] in Morton order (the
// element ranges tile [0, 3·nt) in order), blocks: [{ c0, c1, lo, hi }] (the
// aligned 8 × 8 blocks: cells c0 … c1 − 1) }
export function cellSort(pos, idx, { dy = null, padY = 0, pad = 0.05, cell = CELL_M } = {}) {
  const nt = (idx ? idx.length : pos.length / 3) / 3 | 0;
  if (nt < 2) return null;
  // the triangle's vertices (a soup: its own three)
  const V = idx || null;
  const tc = new Int32Array(2 * nt), inv = 1 / (3 * cell);
  let i0 = Infinity, i1 = -Infinity, j0 = Infinity, j1 = -Infinity;
  for (let t = 0, e = 0; t < nt; t++, e += 3) {
    const a = 3 * (V ? V[e] : e), b = 3 * (V ? V[e + 1] : e + 1), c = 3 * (V ? V[e + 2] : e + 2);
    const i = Math.floor((pos[a] + pos[b] + pos[c]) * inv), j = Math.floor((pos[a + 2] + pos[b + 2] + pos[c + 2]) * inv);
    tc[2 * t] = i; tc[2 * t + 1] = j;
    if (i < i0) i0 = i; if (i > i1) i1 = i; if (j < j0) j0 = j; if (j > j1) j1 = j;
  }
  if (i0 === i1 && j0 === j1) return null;
  if (!(i0 >= -512 && j0 >= -512 && i1 < 512 && j1 < 512)) throw new Error('cellSort: cells out of the key range');
  const w = i1 - i0 + 1, h = j1 - j0 + 1, lc = new Int32Array(nt), cnt = new Int32Array(w * h);
  for (let t = 0; t < nt; t++) { const l = (tc[2 * t + 1] - j0) * w + tc[2 * t] - i0; lc[t] = l; cnt[l]++; }
  const occ = [];
  for (let l = 0; l < w * h; l++) if (cnt[l]) occ.push({ l, key: cellKey(i0 + l % w, j0 + (l / w | 0)) });
  occ.sort((a, b) => a.key - b.key);
  const nc = occ.length, at = new Int32Array(w * h);
  for (let s = 0, o = 0; s < nc; s++) { at[occ[s].l] = o; o += cnt[occ[s].l]; }
  const order = new Uint32Array(nt);
  for (let t = 0; t < nt; t++) order[at[lc[t]]++] = t;
  // each cell's box over its triangles' vertices (a contiguous range now)
  const cells = [], blocks = [];
  for (let s = 0, st = 0; s < nc; s++) {
    const n = cnt[occ[s].l];
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    for (let u = st; u < st + n; u++) {
      const e = 3 * order[u];
      for (let k = 0; k < 3; k++) {
        const v = V ? V[e + k] : e + k, p = 3 * v, x = pos[p], y = pos[p + 1], z = pos[p + 2];
        const d = dy ? dy[v] : 0, ya = d < 0 ? y + d : y, yb = d > 0 ? y + d : y;
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (ya < y0) y0 = ya; if (yb > y1) y1 = yb;
        if (z < z0) z0 = z; if (z > z1) z1 = z;
      }
    }
    const lo = [x0 - pad, y0 - pad, z0 - pad], hi = [x1 + pad, y1 + pad + padY, z1 + pad];
    cells.push({ key: occ[s].key, start: 3 * st, count: 3 * n, lo, hi });
    st += n;
    const b = blocks[blocks.length - 1];
    if (b && cells[b.c0].key >>> BLOCK_BITS === occ[s].key >>> BLOCK_BITS) {
      b.c1 = s + 1;
      for (let k = 0; k < 3; k++) { if (lo[k] < b.lo[k]) b.lo[k] = lo[k]; if (hi[k] > b.hi[k]) b.hi[k] = hi[k]; }
    } else blocks.push({ c0: s, c1: s + 1, lo: lo.slice(), hi: hi.slice() });
  }
  return { order, cells, blocks };
}

// the index in cell order (the same array type)
export function sortedIndex(order, idx) {
  const out = new idx.constructor(idx.length);
  for (let t = 0, e = 0; t < order.length; t++, e += 3) { const s = 3 * order[t]; out[e] = idx[s]; out[e + 1] = idx[s + 1]; out[e + 2] = idx[s + 2]; }
  return out;
}

// a non-indexed attribute array (itemSize n) permuted into cell order, in place
export function permuteTris(order, arr, n) {
  const src = arr.slice(), m = 3 * n;
  for (let t = 0, d = 0; t < order.length; t++) for (let s = order[t] * m, e = s + m; s < e;) arr[d++] = src[s++];
}

// permuteTris undone: an attribute array in cell order back to the old
// triangle order, in place
export function unpermuteTris(order, arr, n) {
  const src = arr.slice(), m = 3 * n;
  for (let t = 0, s = 0; t < order.length; t++) for (let d = order[t] * m, e = d + m; d < e;) arr[d++] = src[s++];
}

// the drawn runs [[start, count]] (elements) of cells whose flag `on` is
// set: adjacent cells merge; then the smallest gaps merge until ≤ maxRuns
// remain (a merged gap draws its cells: a superset, never a hole)
export function cellRuns(cells, on, maxRuns) {
  const runs = [];
  for (let c = 0; c < cells.length; c++) {
    if (!on[c]) continue;
    const last = runs[runs.length - 1], s = cells[c].start, e = s + cells[c].count;
    if (last && last[1] === s) last[1] = e; else runs.push([s, e]);
  }
  if (runs.length > maxRuns) {
    const gaps = [];
    for (let i = 1; i < runs.length; i++) gaps.push([runs[i][0] - runs[i - 1][1], i]);
    gaps.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const cut = new Uint8Array(runs.length);
    for (let g = gaps.length - (maxRuns - 1); g < gaps.length; g++) cut[gaps[g][1]] = 1;
    const m = [];
    for (let i = 0; i < runs.length; i++) { if (i && !cut[i]) m[m.length - 1][1] = runs[i][1]; else m.push([runs[i][0], runs[i][1]]); }
    runs.length = 0; runs.push(...m);
  }
  return runs.map(([s, e]) => [s, e - s]);
}

// a box [lo, hi] meeting one of the world cut boxes (worldcut.js cutBoxes:
// AABBs [x0, x1, z0, z1, y0, y1]), touching counts
export const cutMeets = (lo, hi, boxes) => boxes.some((b) => lo[0] <= b[1] && hi[0] >= b[0]
  && lo[2] <= b[3] && hi[2] >= b[2] && lo[1] <= b[5] && hi[1] >= b[4]);

// CUT-SPLIT (APP_SURFACE_PERF): a unit's cells as draw pieces, the runs of
// cells alike in meeting a cut box → [{ cut, start, count, cells, blocks }]
// (the piece's element range, its cells — absolute starts, as the unit's —
// and its blocks re-cut to it, boxes over its own cells). A cell meeting no
// box holds no fragment the cut could discard: it draws without the loop
export function cutPieces(cs, boxes) {
  const { cells, blocks } = cs, n = cells.length, f = new Uint8Array(n), out = [];
  for (let c = 0; c < n; c++) f[c] = cutMeets(cells[c].lo, cells[c].hi, boxes) ? 1 : 0;
  for (let a = 0, b; a < n; a = b) {
    for (b = a + 1; b < n && f[b] === f[a]; b++);
    const pb = [];
    for (const k of blocks) {
      const c0 = Math.max(k.c0, a), c1 = Math.min(k.c1, b);
      if (c0 >= c1) continue;
      const lo = cells[c0].lo.slice(), hi = cells[c0].hi.slice();
      for (let c = c0 + 1; c < c1; c++) for (let i = 0; i < 3; i++) { lo[i] = Math.min(lo[i], cells[c].lo[i]); hi[i] = Math.max(hi[i], cells[c].hi[i]); }
      pb.push({ c0: c0 - a, c1: c1 - a, lo, hi });
    }
    out.push({ cut: f[a], start: cells[a].start, count: cells[b - 1].start + cells[b - 1].count - cells[a].start, cells: cells.slice(a, b), blocks: pb });
  }
  return out;
}

// a ground-tree Work's arrays (gmdecode.js geoArrays: world pos, the i8
// geomorph code dc, idx) in cell order: a.idx a NEW sorted index (the Work's
// own arrays stay untouched), a.cells the table (null: one cell). The vertex draws at y − (1 − slot)·δ, slot in
// [0, 1] (terrain.js MORPH_DECL_GLSL), so its range is [y − δ, y]: dy = −δ
// (stitch.js decD, the shader's formula; the box pad holds its float32 ulp).
// k: the node's level — a node past L6 (the far tree, up to L9 = 65.5 km)
// takes cells of CELL_M · 2^(k − 6): 64 a side at most, and the key's ±512
// cells reach ±524 km (128 m cells end at ±65.5 km: the L9 nodes W / N of
// the frame threw)
export const groundCellM = (k) => CELL_M * 2 ** Math.max(0, k - 6);
export function groundCells(a, k = 0) {
  const nv = a.pos.length / 3, dy = new Float32Array(nv);
  for (let v = 0; v < nv; v++) dy[v] = -decD(a.dc[v]);
  const cs = cellSort(a.pos, a.idx, { dy, cell: groundCellM(k) });
  if (cs) { a.idx = sortedIndex(cs.order, a.idx); a.cells = { cells: cs.cells, blocks: cs.blocks }; } else a.cells = null;
  return a;
}

// groundm/lookup.js indexNode's grid as flat arrays (R8c4; here, not in
// lookup.js: that file is in the GROUND-M baker's code closure) — a worker posts it with its node
// (transferable; client/src/gmworker.js) — and back. start: Uint32Array
// (cells² + 1), cell c's triangles = tri[start[c] .. start[c + 1]] in
// indexNode's order; gridOf: B[c] a view of tri, undefined where the cell is
// empty, as indexNode leaves it (groundAt reads both alike)
export function packGrid(g) {
  const n = g.cells * g.cells, start = new Uint32Array(n + 1);
  let m = 0;
  for (let c = 0; c < n; c++) { start[c] = m; if (g.B[c]) m += g.B[c].length; }
  start[n] = m;
  const tri = new Uint32Array(m);
  for (let c = 0; c < n; c++) if (g.B[c]) tri.set(g.B[c], start[c]);
  return { cells: g.cells, cs: g.cs, start, tri };
}
export function gridOf(p) {
  const n = p.cells * p.cells, B = new Array(n), { start, tri } = p;
  for (let c = 0; c < n; c++) if (start[c + 1] > start[c]) B[c] = tri.subarray(start[c], start[c + 1]);
  return { cells: p.cells, cs: p.cs, B };
}
